import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator as CalcIcon, MessageCircle } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";

const idr = (n) => "Rp " + new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(n);

export default function Calculator() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ t: "8", w: "12", l: "4", q: "10" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const volume = useMemo(() => {
    const t = parseFloat(form.t) || 0;
    const w = parseFloat(form.w) || 0;
    const l = parseFloat(form.l) || 0;
    const q = parseFloat(form.q) || 0;
    return (t / 100) * (w / 100) * l * q;
  }, [form]);

  const vStr = volume.toFixed(3).replace(".", ",");
  const fields = [
    { k: "t", label: t.calculator.thickness, testid: "calc-thickness" },
    { k: "w", label: t.calculator.width, testid: "calc-width" },
    { k: "l", label: t.calculator.length, testid: "calc-length" },
    { k: "q", label: t.calculator.qty, testid: "calc-qty" },
  ];

  return (
    <section id="kalkulator" className="py-24 sm:py-32 bg-[#0B1220]" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-sky-400 font-semibold flex items-center gap-2">
            <CalcIcon size={14} /> {t.calculator.eyebrow}
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">{t.calculator.title}</h2>
          <p className="mt-4 text-slate-400 text-base sm:text-lg max-w-md">{t.calculator.sub}</p>

          <div className="mt-10 bg-[#101B2E] border border-slate-800 rounded-3xl p-8 text-white relative overflow-hidden blueprint-grid-dark" data-testid="calc-result-panel">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-sky-400">{t.calculator.result}</p>
            <p className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight" data-testid="calc-volume">{vStr} m³</p>
            <div className="wood-grain-line my-6 opacity-60" />
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-sky-400">{t.calculator.estimate}</p>
            <p className="mt-2 font-display font-bold text-xl sm:text-2xl text-wood-cedar" data-testid="calc-estimate">
              {idr(volume * 1525000)} – {idr(volume * 1850000)}
            </p>
            <p className="mt-3 text-xs text-slate-400">{t.calculator.estimateNote}</p>
            <p className="mt-1.5 text-xs font-mono text-sky-300/80" data-testid="calc-ref">{t.calculator.ref}</p>
            <span className="absolute top-6 right-6 bg-sky-500/15 border border-sky-500/40 text-sky-300 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-testid="calc-tier">
              {t.calculator.tier(volume)}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="bg-[#101B2E] border border-slate-800 rounded-3xl p-8 sm:p-10"
        >
          <div className="grid grid-cols-2 gap-5">
            {fields.map((f) => (
              <label key={f.k} className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{f.label}</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form[f.k]}
                  onChange={set(f.k)}
                  data-testid={f.testid}
                  className="mt-2 w-full rounded-xl border border-slate-700 bg-[#0B1220] px-4 py-3.5 font-display font-bold text-lg text-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
                />
              </label>
            ))}
          </div>
          <a
            href={waLink(t.calculator.waMsg(form.t, form.w, form.l, form.q, vStr))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="calc-wa-btn"
            className="mt-8 inline-flex w-full items-center justify-center gap-2.5 bg-sky-500 hover:bg-sky-400 text-white font-semibold px-6 py-4 rounded-2xl transition-colors"
          >
            <MessageCircle size={19} /> {t.calculator.send}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
